"use client";

import {
  addAuthTelemetryBreadcrumb,
  captureAuthFailureTelemetry,
  resolveProxyAuthFlow,
  resolveProxyAuthMethod,
  resolveProxyAuthStage,
  sanitizeAuthRequestTarget,
} from "@/lib/auth-failure-telemetry";

type AseedWindow = Window & {
  __aseedFirebaseFetchProxyInstalled?: boolean;
};

const FIREBASE_PROXY_HOSTS = new Set([
  "identitytoolkit.googleapis.com",
  "securetoken.googleapis.com",
]);

function buildProxyUrl(url: URL): string {
  const host = encodeURIComponent(url.origin);
  const path = encodeURIComponent(`${url.pathname}${url.search}`);
  return `/api/firebase/identitytoolkit?host=${host}&path=${path}`;
}

function shouldProxy(url: URL): boolean {
  return FIREBASE_PROXY_HOSTS.has(url.hostname);
}

function toUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === "string") {
      return new URL(input, window.location.origin);
    }
    if (input instanceof URL) {
      return input;
    }
    return new URL(input.url, window.location.origin);
  } catch {
    return null;
  }
}

function createProxyRequestFromRequest(
  request: Request,
  proxyUrl: string
): Request {
  const method = request.method.toUpperCase();

  return new Request(proxyUrl, {
    method,
    headers: request.headers,
    body: method === "GET" || method === "HEAD" ? undefined : request.body,
    cache: request.cache,
    credentials: request.credentials,
    integrity: request.integrity,
    keepalive: request.keepalive,
    mode: "same-origin",
    redirect: request.redirect,
    referrer: request.referrer,
    referrerPolicy: request.referrerPolicy,
    signal: request.signal,
  });
}

export function installIdentityToolkitProxy(): void {
  if (typeof window === "undefined") {
    return;
  }

  const typedWindow = window as AseedWindow;
  if (typedWindow.__aseedFirebaseFetchProxyInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = toUrl(input);
    if (!url || !shouldProxy(url)) {
      return originalFetch(input as RequestInfo, init);
    }

    const proxyUrl = buildProxyUrl(url);
    const requestTarget = sanitizeAuthRequestTarget(url);
    const authStage = resolveProxyAuthStage(url.hostname);
    const authFlow = resolveProxyAuthFlow(requestTarget);
    const authMethod = resolveProxyAuthMethod(requestTarget);
    const method =
      input instanceof Request
        ? input.method.toUpperCase()
        : init?.method?.toUpperCase() ?? "GET";

    addAuthTelemetryBreadcrumb({
      message: "auth_proxy_request_started",
      data: {
        auth_flow: authFlow,
        auth_method: authMethod,
        auth_stage: authStage,
        request_target: requestTarget,
        method,
      },
    });

    try {
      const response =
        input instanceof Request
          ? await originalFetch(createProxyRequestFromRequest(input, proxyUrl))
          : await originalFetch(proxyUrl, {
              ...init,
              mode: "same-origin",
            });

      if (!response.ok) {
        captureAuthFailureTelemetry({
          authFlow,
          authMethod,
          authStage,
          httpStatus: response.status,
          requestTarget,
        });
      }

      return response;
    } catch (error) {
      captureAuthFailureTelemetry({
        authFlow,
        authMethod,
        authStage,
        error,
        requestTarget,
      });
      throw error;
    }
  };

  typedWindow.__aseedFirebaseFetchProxyInstalled = true;
}
